import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiLock, FiEye, FiEyeOff, FiAlertCircle, FiArrowLeft, FiSlash, FiKey } from 'react-icons/fi';
import TopBar from './TopBar';
import { API_BASE_URL, getAreaGrant, saveAreaGrant } from '../../lib/session';
import { ensureFirebaseArea, withTimeout } from '../../lib/firebase';

// Opening the space's Firestore data must not hold the page forever on a slow server.
const openFirebaseArea = (area) => withTimeout(ensureFirebaseArea(area), 10000);

const CARD =
  'w-full max-w-[420px] rounded-3xl bg-white p-8 text-left ring-1 ring-black/[0.04] shadow-[0_2px_8px_rgba(0,0,0,0.04),0_24px_48px_-12px_rgba(0,0,0,0.16)]';

const IconTile = ({ icon: Icon, tone }) => {
  const tones = {
    brand: 'bg-brand-50 text-brand-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
  };
  return (
    <span className={`flex h-12 w-12 items-center justify-center rounded-2xl ${tones[tone]}`}>
      <Icon size={22} aria-hidden="true" />
    </span>
  );
};

// Sensitive space: opened with the personal access code defined by the administrator.
const AreaGate = ({ area, title, standalone = false, children }) => {
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(false);
  const [status, setStatus] = useState(null);
  const [code, setCode] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [error, setError] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Already unlocked in this tab (e.g. after a reload): reopen the Firestore data, then the page.
    if (getAreaGrant(area)) {
      openFirebaseArea(area).then(() => !cancelled && setUnlocked(true));
      return () => {
        cancelled = true;
      };
    }
    fetch(`${API_BASE_URL}/api/access/${area}`)
      .then((response) => (response.ok ? response.json() : Promise.reject(response)))
      .then((data) => !cancelled && setStatus(data))
      .catch(() => !cancelled && setStatus({ failed: true }));
    return () => {
      cancelled = true;
    };
  }, [area]);

  if (unlocked) return children;

  const unlock = async (e) => {
    e.preventDefault();
    if (isBusy) return;
    setIsBusy(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/access/${area}/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        saveAreaGrant(area, data.grant);
        await openFirebaseArea(area);
        setUnlocked(true);
        return;
      }
      if (data.code === 'NO_CODE') setStatus({ allowed: true, hasCode: false });
      else if (data.code === 'AREA_FORBIDDEN') setStatus({ allowed: false });
      else setError(data.message || 'Code refusé.');
      setCode('');
    } catch (err) {
      setError('Impossible de joindre le serveur. Vérifiez votre connexion, puis réessayez.');
    } finally {
      setIsBusy(false);
    }
  };

  let content;
  if (!status) {
    content = (
      <div className="flex items-center gap-[12px] text-[14px] text-black/50" role="status">
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-solid border-black/10 border-t-brand-600" />
        Vérification de votre accès…
      </div>
    );
  } else if (status.failed) {
    content = (
      <>
        <IconTile icon={FiAlertCircle} tone="red" />
        <h2 className="mb-0 mt-[20px] text-[21px] font-semibold tracking-tight text-ink">Vérification impossible</h2>
        <p className="mb-0 mt-2 text-[14.5px] leading-relaxed text-black/55">
          L’accès à l’espace {title} n’a pas pu être vérifié. Rechargez la page dans quelques instants.
        </p>
      </>
    );
  } else if (!status.allowed) {
    content = (
      <>
        <IconTile icon={FiSlash} tone="red" />
        <h2 className="mb-0 mt-[20px] text-[21px] font-semibold tracking-tight text-ink">Accès non autorisé</h2>
        <p className="mb-0 mt-2 text-[14.5px] leading-relaxed text-black/55">
          L’espace <strong className="font-semibold text-ink">{title}</strong> n’est pas ouvert pour votre
          compte. Si vous en avez besoin, demandez à l’administrateur de l’ajouter à votre fiche.
        </p>
      </>
    );
  } else if (!status.hasCode) {
    content = (
      <>
        <IconTile icon={FiKey} tone="amber" />
        <h2 className="mb-0 mt-[20px] text-[21px] font-semibold tracking-tight text-ink">Code d’accès non défini</h2>
        <p className="mb-0 mt-2 text-[14.5px] leading-relaxed text-black/55">
          L’espace <strong className="font-semibold text-ink">{title}</strong> est protégé par un code
          personnel, que l’administrateur n’a pas encore défini pour votre compte. Demandez-le-lui.
        </p>
      </>
    );
  } else {
    content = (
      <>
        <IconTile icon={FiLock} tone="brand" />
        <p className="mb-0 mt-[20px] text-[12px] font-semibold uppercase tracking-[0.08em] text-brand-600">
          Espace protégé
        </p>
        <h2 className="mb-0 mt-1 text-[21px] font-semibold tracking-tight text-ink">{title}</h2>
        <p className="mb-0 mt-2 text-[14.5px] leading-relaxed text-black/55">
          Saisissez votre code d’accès personnel. L’espace reste ouvert jusqu’à la fermeture de cet onglet.
        </p>
        <form onSubmit={unlock} className="mt-6 space-y-4">
          <div>
            <label htmlFor={`area-code-${area}`} className="mb-1.5 block text-[13px] font-medium text-black/60">
              Code d’accès
            </label>
            <div className="relative">
              <FiKey
                size={17}
                aria-hidden="true"
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-black/35"
              />
              <input
                id={`area-code-${area}`}
                type={showCode ? 'text' : 'password'}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  if (error) setError('');
                }}
                required
                autoFocus
                autoComplete="off"
                disabled={isBusy}
                className="h-12 w-full rounded-xl border-[1px] border-solid border-black/10 bg-white pl-11 pr-12 text-[15px] text-ink outline-none transition placeholder:text-black/30 focus:border-brand-600 focus:ring-4 focus:ring-brand-600/10 disabled:opacity-60"
              />
              <button
                type="button"
                onClick={() => setShowCode((v) => !v)}
                aria-label={showCode ? 'Masquer le code' : 'Afficher le code'}
                className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-lg border-0 bg-[transparent] text-black/40 transition hover:text-black/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40"
              >
                {showCode ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
          </div>
          {error && (
            <div
              role="alert"
              className="flex items-start gap-2.5 rounded-2xl bg-red-50 px-[16px] py-[12px] text-[14px] leading-snug text-red-700"
            >
              <FiAlertCircle size={17} aria-hidden="true" className="mt-px flex-none" />
              <span>{error}</span>
            </div>
          )}
          <button
            type="submit"
            disabled={isBusy}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-full border-0 bg-brand-600 text-[15px] font-medium text-white transition hover:bg-brand-700 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-600/25 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isBusy ? 'Vérification…' : 'Déverrouiller'}
          </button>
        </form>
      </>
    );
  }

  const card = <div className={CARD}>{content}</div>;

  if (!standalone) {
    return <div className="flex justify-center px-[16px] py-10 font-sans">{card}</div>;
  }

  return (
    <div className="min-h-screen bg-mist font-sans">
      <TopBar />
      <main className="flex min-h-[calc(100vh-3rem)] flex-col items-center justify-center px-[20px] py-12">
        <div className="w-full max-w-[420px]">
          <button
            type="button"
            onClick={() => navigate('/role')}
            className="mb-6 flex items-center gap-1.5 rounded-full border-0 bg-[transparent] p-0 text-[14px] font-medium text-brand-600 hover:text-brand-700"
          >
            <FiArrowLeft size={15} aria-hidden="true" />
            Retour au menu
          </button>
          {card}
        </div>
      </main>
    </div>
  );
};

export default AreaGate;
